type TimezoneSource = 'employee' | 'location' | 'store' | 'company' | 'fallback';
type OperationalTimezoneSource = 'location' | 'store' | 'company' | 'system';

export interface EffectiveTimezoneResult {
  tz: string;
  source: TimezoneSource;
}

export interface ResolveOperationalTimeZoneResult {
  timeZone: string;
  source: OperationalTimezoneSource;
}

export interface FormatUtcInstantForDisplayOptions {
  includeSeconds?: boolean;
  emptyFallback?: string;
  invalidFallback?: string;
  allowSystemTimeZoneFallback?: boolean;
}

type TimezoneCarrier = { timezone?: string | null } | null | undefined;

const FALLBACK_TIMEZONE = 'UTC';

export function resolveEffectiveTimezone(input: {
  employee?: TimezoneCarrier;
  location?: TimezoneCarrier;
  store?: TimezoneCarrier;
  company?: TimezoneCarrier;
}): EffectiveTimezoneResult {
  const employeeTz = normalizeTimeZone(input.employee?.timezone);
  if (employeeTz) {
    return { tz: employeeTz, source: 'employee' };
  }

  const locationTz = normalizeTimeZone(input.location?.timezone);
  if (locationTz) {
    return { tz: locationTz, source: 'location' };
  }

  const storeTz = normalizeTimeZone(input.store?.timezone);
  if (storeTz) {
    return { tz: storeTz, source: 'store' };
  }

  const companyTz = normalizeTimeZone(input.company?.timezone);
  if (companyTz) {
    return { tz: companyTz, source: 'company' };
  }

  return { tz: getSystemTimeZone(), source: 'fallback' };
}

export function resolveOperationalTimeZone(input: {
  locationTimezone?: string | null;
  storeTimezone?: string | null;
  companyTimezone?: string | null;
  systemTimezone?: string | null;
}): ResolveOperationalTimeZoneResult {
  const locationTz = normalizeTimeZone(input.locationTimezone);
  if (locationTz) {
    return { timeZone: locationTz, source: 'location' };
  }

  const storeTz = normalizeTimeZone(input.storeTimezone);
  if (storeTz) {
    return { timeZone: storeTz, source: 'store' };
  }

  const companyTz = normalizeTimeZone(input.companyTimezone);
  if (companyTz) {
    return { timeZone: companyTz, source: 'company' };
  }

  const systemTz = normalizeTimeZone(input.systemTimezone) ?? getSystemTimeZone();
  return { timeZone: systemTz, source: 'system' };
}

export function getSystemTimeZone(): string {
  const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (typeof resolved === 'string' && resolved.trim().length > 0) {
    return resolved;
  }

  return FALLBACK_TIMEZONE;
}

export function listSupportedTimeZones(): string[] {
  const intlWithSupported = Intl as typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
  };
  const supported = typeof intlWithSupported.supportedValuesOf === 'function'
    ? intlWithSupported.supportedValuesOf('timeZone')
    : [];

  if (supported.length > 0) {
    return supported.slice().sort();
  }

  const fallback = getSystemTimeZone();
  const options = new Set<string>([fallback, FALLBACK_TIMEZONE]);
  return Array.from(options);
}

export function formatDateTimeInputValue(iso: string, timeZone: string): string {
  const date = toDate(iso);
  if (!date) {
    return '';
  }

  const resolvedTimeZone = normalizeTimeZone(timeZone);
  if (!resolvedTimeZone) {
    return '';
  }

  const parts = getZonedDateParts(date, resolvedTimeZone);
  const year = String(parts.year).padStart(4, '0');
  const month = String(parts.month).padStart(2, '0');
  const day = String(parts.day).padStart(2, '0');
  const hour = String(parts.hour).padStart(2, '0');
  const minute = String(parts.minute).padStart(2, '0');

  return `${year}-${month}-${day}T${hour}:${minute}`;
}

export function parseDateTimeInputValue(value: string, timeZone: string): Date | null {
  if (!value) {
    return null;
  }

  const resolvedTimeZone = resolveTimeZoneForOperationalUse(timeZone);
  if (!resolvedTimeZone) {
    return null;
  }

  const parts = parseLocalDateTimeParts(value);
  if (!parts) {
    return null;
  }

  const parsed = zonedTimeToUtc(parts, resolvedTimeZone);
  const roundTrip = getZonedDateParts(parsed, resolvedTimeZone);
  if (
    roundTrip.year !== parts.year ||
    roundTrip.month !== parts.month ||
    roundTrip.day !== parts.day ||
    roundTrip.hour !== parts.hour ||
    roundTrip.minute !== parts.minute ||
    roundTrip.second !== parts.second
  ) {
    return null;
  }

  return parsed;
}

export function parseLocalDateTimeToUtcIso(localDateTime: string, timeZone: string): string | null {
  const parsed = parseDateTimeInputValue(localDateTime, timeZone);
  return parsed ? parsed.toISOString() : null;
}

export function formatTimeInTimeZone(iso: string, timeZone: string): string {
  const date = toDate(iso);
  if (!date) {
    return '';
  }

  const resolvedTimeZone = normalizeTimeZone(timeZone);
  if (!resolvedTimeZone) {
    return '';
  }

  return date.toLocaleTimeString(undefined, { timeZone: resolvedTimeZone });
}

export function formatDateTimeInTimeZone(iso: string, timeZone: string): string {
  const date = toDate(iso);
  if (!date) {
    return '';
  }

  const resolvedTimeZone = normalizeTimeZone(timeZone);
  if (!resolvedTimeZone) {
    return '';
  }

  return date.toLocaleString(undefined, { timeZone: resolvedTimeZone });
}

export function formatUtcInstantForDisplay(
  isoUtc: string | null | undefined,
  timeZone: string,
  options?: FormatUtcInstantForDisplayOptions,
): string {
  const emptyFallback = options?.emptyFallback ?? '--';
  if (!isoUtc) {
    return emptyFallback;
  }

  const parsed = toDate(isoUtc);
  if (!parsed) {
    return options?.invalidFallback ?? 'Invalid date';
  }

  const resolvedTimeZone = resolveTimeZoneForOperationalUse(
    timeZone,
    options?.allowSystemTimeZoneFallback === true,
  );
  if (!resolvedTimeZone) {
    return options?.invalidFallback ?? 'Invalid date';
  }

  const parts = getZonedDateParts(parsed, resolvedTimeZone);
  const year = String(parts.year).padStart(4, '0');
  const month = String(parts.month).padStart(2, '0');
  const day = String(parts.day).padStart(2, '0');
  const hour = String(parts.hour).padStart(2, '0');
  const minute = String(parts.minute).padStart(2, '0');
  const second = String(parts.second).padStart(2, '0');

  if (options?.includeSeconds === false) {
    return `${year}-${month}-${day} ${hour}:${minute}`;
  }

  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
}

export function toUtcRangeFromLocalDate(
  localDateYYYYMMDD: string,
  timeZone: string,
  options?: { allowSystemTimeZoneFallback?: boolean },
): { startInclusiveIso: string; endExclusiveIso: string } {
  const match = localDateYYYYMMDD.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    throw new Error(`Invalid local date: ${localDateYYYYMMDD}`);
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    throw new Error(`Invalid local date: ${localDateYYYYMMDD}`);
  }

  const resolvedTimeZone = resolveTimeZoneForOperationalUse(
    timeZone,
    options?.allowSystemTimeZoneFallback === true,
  );
  if (!resolvedTimeZone) {
    throw new Error(`Invalid operational time zone: ${timeZone}`);
  }

  const start = zonedTimeToUtc(
    {
      year,
      month,
      day,
      hour: 0,
      minute: 0,
      second: 0,
    },
    resolvedTimeZone,
  );

  const nextUtcDate = new Date(Date.UTC(year, month - 1, day));
  nextUtcDate.setUTCDate(nextUtcDate.getUTCDate() + 1);
  const endExclusive = zonedTimeToUtc(
    {
      year: nextUtcDate.getUTCFullYear(),
      month: nextUtcDate.getUTCMonth() + 1,
      day: nextUtcDate.getUTCDate(),
      hour: 0,
      minute: 0,
      second: 0,
    },
    resolvedTimeZone,
  );

  return {
    startInclusiveIso: start.toISOString(),
    endExclusiveIso: endExclusive.toISOString(),
  };
}

export function getTodayUtcRange(timeZone: string, now = new Date()): { start: Date; end: Date } {
  const parts = getZonedDateParts(now, timeZone);
  const start = zonedTimeToUtc(
    {
      year: parts.year,
      month: parts.month,
      day: parts.day,
      hour: 0,
      minute: 0,
      second: 0,
    },
    timeZone,
  );

  const nextLocalDay = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  nextLocalDay.setUTCDate(nextLocalDay.getUTCDate() + 1);

  const nextStart = zonedTimeToUtc(
    {
      year: nextLocalDay.getUTCFullYear(),
      month: nextLocalDay.getUTCMonth() + 1,
      day: nextLocalDay.getUTCDate(),
      hour: 0,
      minute: 0,
      second: 0,
    },
    timeZone,
  );

  return { start, end: new Date(nextStart.getTime() - 1) };
}

function normalizeTimeZone(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (!isValidIanaTimeZone(trimmed)) {
    return null;
  }

  return trimmed;
}

function resolveTimeZoneForOperationalUse(
  value: string | null | undefined,
  allowSystemTimeZoneFallback = false,
): string | null {
  const normalized = normalizeTimeZone(value);
  if (normalized) {
    return normalized;
  }

  return allowSystemTimeZoneFallback ? getSystemTimeZone() : null;
}

function parseLocalDateTimeParts(value: string): DateParts | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) {
    return null;
  }

  const [, yearPart, monthPart, dayPart, hourPart, minutePart, secondPart] = match as [
    string,
    string,
    string,
    string,
    string,
    string,
    string?,
  ];
  const year = Number(yearPart);
  const month = Number(monthPart);
  const day = Number(dayPart);
  const hour = Number(hourPart);
  const minute = Number(minutePart);
  const second = Number(secondPart ?? 0);

  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    !Number.isInteger(second)
  ) {
    return null;
  }

  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59 ||
    second < 0 ||
    second > 59
  ) {
    return null;
  }

  const calendarProbe = new Date(Date.UTC(year, month - 1, day));
  if (
    calendarProbe.getUTCFullYear() !== year ||
    calendarProbe.getUTCMonth() + 1 !== month ||
    calendarProbe.getUTCDate() !== day
  ) {
    return null;
  }

  return {
    year,
    month,
    day,
    hour,
    minute,
    second,
  };
}

function isValidIanaTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

interface DateParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function getZonedDateParts(date: Date, timeZone: string): DateParts {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  const parts = formatter.formatToParts(date);
  const getPart = (type: Intl.DateTimeFormatPartTypes): string => {
    const part = parts.find((candidate) => candidate.type === type)?.value;
    if (!part) {
      throw new Error(`Missing Intl.DateTimeFormat part: ${type}`);
    }
    return part;
  };

  return {
    year: Number(getPart('year')),
    month: Number(getPart('month')),
    day: Number(getPart('day')),
    hour: Number(getPart('hour')),
    minute: Number(getPart('minute')),
    second: Number(getPart('second')),
  };
}

function getTimeZoneOffsetMs(date: Date, timeZone: string): number {
  const parts = getZonedDateParts(date, timeZone);
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return asUtc - date.getTime();
}

function zonedTimeToUtc(parts: DateParts, timeZone: string): Date {
  const utcGuess = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  let utc = utcGuess;
  let lastOffset: number | null = null;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const offset = getTimeZoneOffsetMs(new Date(utc), timeZone);
    if (offset === lastOffset) {
      break;
    }
    lastOffset = offset;
    utc = utcGuess - offset;
  }

  return new Date(utc);
}

function toDate(iso: string): Date | null {
  if (!iso) {
    return null;
  }

  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}
